import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  arrayUnion,
} from "firebase/firestore";
import { useUserData } from "../context/UserDataFetcher";
import type { Dealer } from "../types/DB2";

type DealersCtx = {
  dealers: Dealer[];
  refresh: () => Promise<void>;
  createDealer: (payload: Partial<Dealer>) => Promise<string>; // returns new doc id
  updateDealer: (id: string, payload: Partial<Dealer>) => Promise<void>;
  deleteDealer: (id: string) => Promise<void>;
};

const DealersContext = createContext<DealersCtx | undefined>(undefined);

export const DealersDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const userData = useUserData();
  const db = getFirestore();

  const linkDealerToAccountOwner = async (dealerDocId: string, accountOwner?: unknown) => {
    const mf = userData?.manufacturerId;
    const uid = typeof accountOwner === "string" ? accountOwner.trim() : "";
    if (!uid) return;
    try {
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);
      const existing = userSnap.exists() ? (userSnap.data() as Record<string, any>) : undefined;
      if (existing?.dealerId && existing.dealerId !== dealerDocId) {
        console.warn("[DealersDataProvider] accountOwner already linked to different dealer", {
          uid,
          existingDealerId: existing.dealerId,
          newDealerId: dealerDocId,
        });
      }
      const updatePayload: Record<string, any> = { dealerId: dealerDocId };
      if (mf && (!existing?.manufacturerId || existing.manufacturerId === mf)) {
        updatePayload.manufacturerId = mf;
      }
      await setDoc(userRef, updatePayload, { merge: true });
      console.log("[DealersDataProvider] linked account owner to dealer", { uid, dealerId: dealerDocId });
    } catch (err) {
      console.error("[DealersDataProvider] failed to link account owner to dealer", {
        dealerId: dealerDocId,
        accountOwner: uid,
        error: err,
      });
    }
  };

  const fetchDealers = async () => {
    if (!userData) {
      setDealers([]);
      return;
    }

    try {
      const dealersRef = collection(db, "dealers");

      // Manufacturer/admin: show dealers tied to this manufacturer
      if (userData.isAdmin || userData.role === "manufacturer") {
        if (!userData.manufacturerId) {
          setDealers([]);
          return;
        }
        // Firestore can only query array-contains for a single value
        const q = query(dealersRef, where("manufacturerIds", "array-contains", userData.manufacturerId));
        const snap = await getDocs(q);
        const list: Dealer[] = [];
        snap.forEach((d) => list.push({ id: d.id, ...(d.data() as any) } as Dealer));
        setDealers(list);
        return;
      }

      // Dealer user: fetch only their own dealer doc by id
      if (userData.role === "dealer" && userData.dealerId) {
        const dref = doc(db, "dealers", userData.dealerId);
        const dsnap = await getDoc(dref);
        if (dsnap.exists()) {
          setDealers([{ id: dsnap.id, ...(dsnap.data() as any) } as Dealer]);
        } else {
          setDealers([]);
        }
        return;
      }

      // Fallback
      setDealers([]);
    } catch (err) {
      console.error("DealersDataProvider: fetchDealers failed", err);
      setDealers([]);
    }
  };

  useEffect(() => {
    fetchDealers();
  }, [userData?.manufacturerId, userData?.dealerId, userData?.role]);

  const sanitizeDocId = (raw: string) => raw.replace(/\//g, "-").replace(/\s+/g, "-").trim();

  const createDealer = async (payload: Partial<Dealer>): Promise<string> => {
    const dealersRef = collection(db, "dealers");
    const mf = userData?.manufacturerId;
    const payloadToSave: Record<string, any> = { ...(payload as any) };

    // prune empty from payload
    Object.keys(payloadToSave).forEach((k) => {
      const v = payloadToSave[k];
      if (v === "" || v === undefined || v === null) delete payloadToSave[k];
    });

    try {
      console.log("[DealersDataProvider] createDealer start", { mf, payload: payloadToSave });
      // Existence check by contact email first, then by name
      const accountOwnerId = typeof payloadToSave.accountOwner === "string" ? payloadToSave.accountOwner : null;
      let existingId: string | null = null;
      const email = payloadToSave?.contactInfo?.email as string | undefined;
      if (email && email.trim().length) {
        try {
          const q1 = query(dealersRef, where("contactInfo.email", "==", email.trim()));
          const s1 = await getDocs(q1);
          if (!s1.empty) {
            existingId = s1.docs[0].id;
          }
        } catch (err) {
          console.warn("[DealersDataProvider] email duplicate check skipped due to permissions", {
            error: err,
          });
        }
      }
      if (!existingId && payloadToSave.name && String(payloadToSave.name).trim().length) {
        try {
          const q2 = query(dealersRef, where("name", "==", String(payloadToSave.name).trim()));
          const s2 = await getDocs(q2);
          if (!s2.empty) {
            existingId = s2.docs[0].id;
          }
        } catch (err) {
          console.warn("[DealersDataProvider] name duplicate check skipped due to permissions", {
            error: err,
          });
        }
      }

      if (existingId) {
        const dref = doc(db, "dealers", existingId);
        try {
          console.log("[DealersDataProvider] dealer exists; merging", { existingId });
          // Merge in any new non-empty fields
          await setDoc(dref, payloadToSave, { merge: true });
          // Add manufacturer id to manufacturerIds array in background
          if (mf) {
            await updateDoc(dref, { manufacturerIds: arrayUnion(mf) });
          }
          await fetchDealers();
          if (accountOwnerId) {
            await linkDealerToAccountOwner(existingId, accountOwnerId);
          }
          return existingId;
        } catch (mergeErr: any) {
          console.error("[DealersDataProvider] merge/link existing dealer failed", { existingId, error: mergeErr });
          throw mergeErr;
        }
      }

      // Create new dealer and seed manufacturerIds with current manufacturer
      if (mf) {
        payloadToSave.manufacturerIds = [mf];
      } else {
        payloadToSave.manufacturerIds = [];
      }

      const businessName = String(payloadToSave.name || "").trim();
      if (!businessName) {
        throw new Error("invalid-argument: Dealer business name is required");
      }
      const docId = sanitizeDocId(businessName);
      const targetRef = doc(db, "dealers", docId);
      await setDoc(targetRef, payloadToSave, { merge: true });
      console.log("[DealersDataProvider] dealer created", { id: docId });
      await fetchDealers();
      if (accountOwnerId) {
        await linkDealerToAccountOwner(docId, accountOwnerId);
      }
      return docId;
    } catch (err) {
      console.error("DealersDataProvider: createDealer failed", err);
      throw err;
    }
  };

  const updateDealer = async (id: string, payload: Partial<Dealer>) => {
    try {
      const dref = doc(db, "dealers", id);
      const toSave: Record<string, any> = { ...(payload as any) };
      const accountOwnerId = typeof toSave.accountOwner === "string" ? toSave.accountOwner : undefined;
      // prune empty
      Object.keys(toSave).forEach((k) => {
        const v = toSave[k];
        if (v === "" || v === undefined || v === null) delete toSave[k];
      });
      // handle nested manufacturerDealerNumbers safely via field paths
      const nested = toSave.manufacturerDealerNumbers as Record<string, string> | undefined;
      if (nested && typeof nested === "object") {
        const nestedUpdates: Record<string, any> = {};
        for (const [k, v] of Object.entries(nested)) {
          if (v !== undefined && v !== null && String(v).trim().length) {
            nestedUpdates[`manufacturerDealerNumbers.${k}`] = v;
          }
        }
        delete toSave.manufacturerDealerNumbers;
        if (Object.keys(toSave).length) await updateDoc(dref, toSave);
        if (Object.keys(nestedUpdates).length) await updateDoc(dref, nestedUpdates);
      } else {
        await updateDoc(dref, toSave);
      }
      await fetchDealers();
      if (accountOwnerId) {
        await linkDealerToAccountOwner(id, accountOwnerId);
      }
    } catch (err) {
      console.error("DealersDataProvider: updateDealer failed", err);
      throw err;
    }
  };

  const deleteDealer = async (id: string) => {
    try {
      const dref = doc(db, "dealers", id);
      await deleteDoc(dref);
      await fetchDealers();
    } catch (err) {
      console.error("DealersDataProvider: deleteDealer failed", err);
      throw err;
    }
  };

  return (
    <DealersContext.Provider
      value={{ dealers, refresh: fetchDealers, createDealer, updateDealer, deleteDealer }}
    >
      {children}
    </DealersContext.Provider>
  );
};

export const useDealersData = () => {
  const ctx = useContext(DealersContext);
  if (!ctx) throw new Error("useDealersData must be used within DealersDataProvider");
  return ctx;
};

export default DealersDataProvider;
